import type { PrismaClient } from '@prisma/client';
import Decimal from 'decimal.js';
import { Router, type Request } from 'express';
import type { Config } from '../../core/config.js';
import { asyncHandler } from '../../core/http.js';
import { authenticate, permissionsGranted } from '../auth/auth.middleware.js';
import { ageBucket, outstanding, type AgeBucket } from '../sales/calc.js';
import { isoOf, toDate } from '../sales/documents.service.js';
import { incomeStatement } from '../reports/reports.service.js';

interface Deps {
  db: PrismaClient;
  config: Config;
}

const OPEN = ['approved', 'sent', 'partially_paid'];
const fx = (v: Decimal) => v.toFixed(0);

/**
 * Dashboard (PLAN.md 7.1). Every section is computed only if the user holds the permission behind it, so a
 * Sales user never receives the books. Amounts are UGX (base currency).
 */
export function createDashboardRouter({ db, config }: Deps) {
  const router = Router();
  router.use('/dashboard', authenticate({ db, config }));
  const can = (req: Request, p: string) => permissionsGranted(req.auth!.permissions, p);

  router.get(
    '/dashboard/summary',
    asyncHandler(async (req, res) => {
      const auth = req.auth!;
      const companyId = auth.companyId;
      const own = auth.scope === 'own';
      const now = new Date();
      const todayIso = now.toISOString().slice(0, 10);
      const today = toDate(todayIso);
      const out: Record<string, unknown> = { generatedAt: now.toISOString(), permissions: {} };
      const perms = out.permissions as Record<string, boolean>;

      // ── cash
      if (can(req, 'banking.account.view')) {
        perms.cash = true;
        const accounts = await db.financialAccount.findMany({
          where: { companyId, status: 'active' },
        });
        const bal = await db.journalLine.groupBy({
          by: ['accountId'],
          where: {
            accountId: { in: accounts.map((a) => a.ledgerAccountId) },
            entry: { companyId, status: 'posted' },
          },
          _sum: { debit: true, credit: true },
        });
        const rows = accounts.map((a) => {
          const b = bal.find((x) => x.accountId === a.ledgerAccountId);
          return {
            id: a.id,
            name: a.name,
            kind: a.kind,
            currency: a.currency,
            balance: fx(
              new Decimal((b?._sum.debit ?? 0).toString()).minus((b?._sum.credit ?? 0).toString()),
            ),
          };
        });
        out.cash = {
          total: fx(rows.reduce((s, r) => s.plus(r.balance), new Decimal(0))),
          accounts: rows,
        };
      }

      // ── receivables, ageing, overdue and approval lists
      if (can(req, 'sales.invoice.view')) {
        perms.receivables = true;
        const invoices = await db.salesDocument.findMany({
          where: {
            companyId,
            docType: 'invoice',
            status: { in: OPEN },
            ...(own ? { ownerId: auth.userId } : {}),
          },
        });
        const names = new Map(
          (
            await db.party.findMany({
              where: { id: { in: [...new Set(invoices.map((i) => i.partyId))] } },
              select: { id: true, name: true },
            })
          ).map((p) => [p.id, p.name]),
        );
        const buckets: Record<AgeBucket, Decimal> = {
          current: new Decimal(0),
          d1_30: new Decimal(0),
          d31_60: new Decimal(0),
          d61_90: new Decimal(0),
          d90_plus: new Decimal(0),
        };
        let total = new Decimal(0);
        let overdue = new Decimal(0);
        const overdueRows: {
          id: number;
          number: string | null;
          partyName: string;
          dueDate: string | null;
          daysOverdue: number;
          outstanding: string;
          currency: string;
        }[] = [];
        for (const inv of invoices) {
          const base = outstanding(inv)
            .times(inv.exchangeRate.toString())
            .toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
          total = total.plus(base);
          const b = ageBucket(inv.dueDate, today);
          buckets[b] = buckets[b].plus(base);
          if (inv.dueDate && inv.dueDate < today) {
            overdue = overdue.plus(base);
            overdueRows.push({
              id: inv.id,
              number: inv.number,
              partyName: names.get(inv.partyId) ?? '',
              dueDate: isoOf(inv.dueDate),
              daysOverdue: Math.round((today.getTime() - inv.dueDate.getTime()) / 86_400_000),
              outstanding: outstanding(inv).toFixed(4),
              currency: inv.currency,
            });
          }
        }
        out.receivables = {
          outstanding: fx(total),
          overdue: fx(overdue),
          overdueCount: overdueRows.length,
          openCount: invoices.length,
          ageing: Object.fromEntries(Object.entries(buckets).map(([k, v]) => [k, fx(v)])),
        };
        out.overdueInvoices = overdueRows.sort((a, b) => b.daysOverdue - a.daysOverdue).slice(0, 5);
        if (can(req, 'sales.invoice.approve')) {
          const pending = await db.salesDocument.findMany({
            where: {
              companyId,
              status: 'pending_approval',
              docType: { in: ['invoice', 'creditnote'] },
              ...(own ? { ownerId: auth.userId } : {}),
            },
            orderBy: { id: 'desc' },
            take: 5,
          });
          const pn = new Map(
            (
              await db.party.findMany({
                where: { id: { in: pending.map((i) => i.partyId) } },
                select: { id: true, name: true },
              })
            ).map((p) => [p.id, p.name]),
          );
          out.awaitingApproval = pending.map((d) => ({
            id: d.id,
            type: d.docType,
            partyName: pn.get(d.partyId) ?? '',
            total: d.total.toFixed(4),
            currency: d.currency,
          }));
        }
      }

      // ── payables
      if (can(req, 'purchases.bill.view')) {
        perms.payables = true;
        const week = new Date(today.getTime() + 7 * 86_400_000);
        const bills = await db.vendorBill.findMany({
          where: {
            companyId,
            status: { in: ['approved', 'partially_paid'] },
            ...(own ? { ownerId: auth.userId } : {}),
          },
        });
        let dueWeek = new Decimal(0);
        let overdue = new Decimal(0);
        let total = new Decimal(0);
        for (const b of bills) {
          const base = new Decimal(b.total.toString())
            .minus(b.amountPaid.toString())
            .times(b.exchangeRate.toString())
            .toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
          total = total.plus(base);
          if (b.dueDate && b.dueDate < today) overdue = overdue.plus(base);
          else if (b.dueDate && b.dueDate <= week) dueWeek = dueWeek.plus(base);
        }
        out.payables = {
          outstanding: fx(total),
          dueThisWeek: fx(dueWeek),
          overdue: fx(overdue),
          openCount: bills.length,
        };
      }

      // ── this month vs last 12 months (needs the books)
      if (can(req, 'accounting.report.view')) {
        perms.books = true;
        const y = now.getUTCFullYear();
        const m = now.getUTCMonth();
        const series: { month: string; revenue: number; expenses: number }[] = [];
        for (let i = 11; i >= 0; i -= 1) {
          const start = new Date(Date.UTC(y, m - i, 1));
          const end = new Date(Date.UTC(y, m - i + 1, 0));
          const r = await incomeStatement(db, companyId, { from: isoOf(start)!, to: isoOf(end)! });
          const expenses = new Decimal(r.totals.costOfSales!.amount)
            .plus(r.totals.operatingExpenses!.amount)
            .plus(r.totals.otherExpenses!.amount);
          series.push({
            month: isoOf(start)!.slice(0, 7),
            revenue: Number(
              new Decimal(r.totals.revenue!.amount).plus(r.totals.otherIncome!.amount),
            ),
            expenses: Number(expenses),
          });
        }
        const cur = series[series.length - 1]!;
        out.month = {
          revenue: cur.revenue,
          expenses: cur.expenses,
          profit: cur.revenue - cur.expenses,
        };
        out.series = series;
      }

      // ── CRM
      if (can(req, 'crm.lead.view')) {
        perms.crm = true;
        const leads = await db.lead.findMany({
          where: { companyId, ...(own ? { ownerId: auth.userId } : {}) },
        });
        const stages = await db.pipelineStage.findMany({ where: { pipeline: { companyId } } });
        const rates = new Map(
          (
            await db.exchangeRate.findMany({
              where: { companyId },
              orderBy: { effectiveDate: 'asc' },
            })
          ).map((r) => [r.currency, new Decimal(r.rate.toString())]),
        );
        const baseOf = (l: { estimatedValue: { toString(): string }; currency: string }) =>
          new Decimal(l.estimatedValue.toString()).times(
            l.currency === 'UGX' ? 1 : (rates.get(l.currency) ?? 1),
          );
        const byStage = new Map<string, { count: number; value: Decimal }>();
        let weighted = new Decimal(0);
        for (const l of leads.filter((x) => x.status === 'open')) {
          const st = stages.find((s) => s.id === l.stageId);
          const key = st?.name ?? '?';
          const e = byStage.get(key) ?? { count: 0, value: new Decimal(0) };
          e.count += 1;
          e.value = e.value.plus(baseOf(l));
          byStage.set(key, e);
          weighted = weighted.plus(
            baseOf(l)
              .times(st?.probability ?? 0)
              .div(100),
          );
        }
        const since = new Date(now.getTime() - 90 * 86_400_000);
        const recent = leads.filter((l) => l.createdAt >= since);
        const won = recent.filter((l) => l.status === 'won').length;
        const closed = recent.filter((l) => l.status !== 'open').length;
        out.pipeline = {
          openCount: leads.filter((l) => l.status === 'open').length,
          weightedValue: fx(weighted),
          byStage: [...byStage.entries()].map(([stage, v]) => ({
            stage,
            count: v.count,
            value: fx(v.value),
          })),
          conversion: {
            created: recent.length,
            won,
            lost: recent.filter((l) => l.status === 'lost').length,
            winRate: closed ? Math.round((won / closed) * 100) : null,
          },
        };
        const due = await db.lead.findMany({
          where: {
            companyId,
            status: 'open',
            nextFollowUpDate: { lte: today },
            ...(own ? { ownerId: auth.userId } : {}),
          },
          orderBy: { nextFollowUpDate: 'asc' },
          take: 5,
          select: { id: true, name: true, nextFollowUpDate: true },
        });
        out.followUpsToday = due.map((l) => ({
          id: l.id,
          name: l.name,
          dueDate: isoOf(l.nextFollowUpDate),
        }));
      }

      // ── renewals
      if (can(req, 'sales.recurring.view')) {
        perms.renewals = true;
        const in30 = new Date(today.getTime() + 30 * 86_400_000);
        const rows = await db.renewal.findMany({
          where: {
            companyId,
            status: 'active',
            expiryDate: { lte: in30 },
            ...(own ? { ownerId: auth.userId } : {}),
          },
          orderBy: { expiryDate: 'asc' },
          take: 5,
        });
        const count = await db.renewal.count({
          where: {
            companyId,
            status: 'active',
            expiryDate: { lte: in30 },
            ...(own ? { ownerId: auth.userId } : {}),
          },
        });
        const pn = new Map(
          (
            await db.party.findMany({
              where: { id: { in: rows.map((r) => r.partyId) } },
              select: { id: true, name: true },
            })
          ).map((p) => [p.id, p.name]),
        );
        out.renewals = {
          dueIn30Days: count,
          next: rows.map((r) => ({
            id: r.id,
            identifier: r.identifier,
            service: r.service,
            partyName: pn.get(r.partyId) ?? '',
            expiryDate: isoOf(r.expiryDate),
          })),
        };
      }

      res.json(out);
    }),
  );

  return router;
}
